import { Check, MapPin } from 'lucide-react';
import type { PickStopDto } from '@eombor/contracts';
import { Badge, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { LayoutCell, LayoutRack } from '../../warehouse/api/warehouse.api.js';

/**
 * JAVON XARITASI — «mahsulot javonning QAYERIDA turibdi».
 *
 * Nega ombor bo'limining fasadi (`RackFacade`) bu yerda YARAMADI: u
 * QURUVCHINING chizmasi. Unda har katakning o'lchami (`200×150`),
 * javonning chuqurligi va balandligi, bo'sh kataklar soni va to'lganlik
 * foizi turadi — javon quradigan odamga kerakli hamma narsa. Yig'ib
 * olayotgan odamga esa bularning BIRORTASI kerak emas va ular kerakli
 * yagona katakni ko'zdan yashirardi.
 *
 * Bu yerda bitta savol bor: «qaysi katak?». Shuning uchun kerakli katak
 * to'la rangda, matn bilan va halqa ichida turadi, qolganlari esa
 * shaklsiz so'niq bloklar — ular faqat MO'LJAL beradi: qaysi qator,
 * chapdanmi yoki o'ngdan.
 */

/** Qatorlarga ajratadi, YUQORI QATOR TEPADA — javonning o'zi kabi. */
const byLevel = (cells: readonly LayoutCell[]): { level: number; cells: LayoutCell[] }[] => {
  const levels = new Map<number, LayoutCell[]>();
  for (const cell of cells) {
    const row = levels.get(cell.level);
    if (row === undefined) levels.set(cell.level, [cell]);
    else row.push(cell);
  }
  return [...levels.entries()]
    .map(([level, row]) => ({ level, cells: [...row].sort((a, b) => a.idx - b.idx) }))
    .sort((a, b) => b.level - a.level);
};

/** Katak kodining qisqa shakli: `02-01`. */
const shortCode = (cell: LayoutCell): string =>
  `${String(cell.level).padStart(2, '0')}-${String(cell.idx).padStart(2, '0')}`;

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

export interface PickRackMapProps {
  rack: LayoutRack;
  /** Zona kodi — sarlavhada, katak kodining birinchi bo'lagi. */
  zoneCode: string;
  /** Shu javondagi to'xtashlar — katak `id` si bo'yicha. */
  stops: ReadonlyMap<string, { stop: PickStopDto; taken: boolean }>;
  /** HOZIR kutilayotgan katak — u yonib turadi. */
  targetCellId: string | null;
  onSelectCell: (cellId: string) => void;
}

export const PickRackMap = ({
  rack,
  zoneCode,
  stops,
  targetCellId,
  onSelectCell,
}: PickRackMapProps) => {
  const rows = byLevel(rack.cells);

  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <figcaption className="flex flex-wrap items-center gap-2">
        <MapPin size={16} aria-hidden className="text-text-faint shrink-0" />
        <span className="text-base font-semibold text-text">
          {t('pick.rackTitle', { zone: zoneCode, rack: rack.code })}
        </span>
        {rack.name === null || rack.name === '' ? null : (
          <span className="truncate text-sm text-text-muted">{rack.name}</span>
        )}
      </figcaption>

      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <div key={row.level} className="flex items-stretch gap-2">
            {/* Qator raqami — javonning yon ustuni. Skladchi «ikkinchi
                qator» deb sanaydi, kod bilan emas. */}
            <span
              className={cn(
                'flex w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
                'bg-surface-2 text-sm font-semibold tabular-nums text-text-muted',
              )}
            >
              {row.level}
            </span>

            <div className="grid min-w-0 flex-1 auto-cols-fr grid-flow-col gap-1.5">
              {row.cells.map((cell) => {
                const found = stops.get(cell.id);
                const target = cell.id === targetCellId;
                const taken = found?.taken === true;

                return (
                  <button
                    key={cell.id}
                    type="button"
                    disabled={found === undefined}
                    onClick={() => onSelectCell(cell.id)}
                    aria-current={target ? 'location' : undefined}
                    className={cn(
                      'flex min-w-0 flex-col items-center justify-center gap-0.5',
                      'rounded-[var(--radius-sm)] border px-1 py-3 text-center',
                      'transition-[background-color,border-color] duration-[var(--motion-base)]',
                      found === undefined
                        ? /* Begona katak — SHAKLSIZ so'niq blok. Kod ham
                             yozilmaydi: u faqat mo'ljal beradi (qaysi
                             qator, chapdanmi) va matn bo'lsa kerakli
                             katak ular orasida yo'qolardi. */
                          'border-border/60 bg-surface-2/60 cursor-default'
                        : taken
                          ? 'border-success/40 bg-success-soft'
                          : target
                            ? 'border-warn bg-warn-soft ring-2 ring-warn/40 motion-safe:animate-pulse'
                            : 'border-accent/40 bg-accent-soft',
                    )}
                  >
                    {found === undefined ? (
                      <span aria-hidden className="text-[0.6rem] text-text-faint/50 tabular-nums">
                        {shortCode(cell)}
                      </span>
                    ) : (
                      <>
                        <span className="flex items-center gap-1">
                          {taken ? (
                            <Check size={13} aria-hidden className="text-success" />
                          ) : null}
                          <span className="text-sm font-semibold tabular-nums text-text">
                            {shortCode(cell)}
                          </span>
                        </span>
                        <span className="w-full truncate text-xs text-text">
                          {found.stop.nomenclatureName}
                        </span>
                        <span className="text-xs font-medium tabular-nums text-text-muted">
                          {round3(found.stop.qty)} {unitText(found.stop.unit)}
                        </span>
                        <Badge tone={taken ? 'success' : target ? 'warn' : 'accent'}>
                          {t('pick.stopOf', { order: found.stop.order })}
                        </Badge>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
};
